/**
 * Format utility functions
 * Provides consistent formatting across the application
 */

import {
  getExplorerAddressUrl as coreGetExplorerAddressUrl,
  getExplorerTxUrl as coreGetExplorerTxUrl,
} from '../constants/chains';
import { formatTokenAmount as formatAmountWithDecimals } from './amountUtils';
import {
  ClientDateTimeOptions,
  DateInput,
  RelativeTimeOptions,
  formatClientDate,
  formatClientDateTime,
  formatClientTime,
  formatRelativeTimeFromNow,
} from './dateTimeUtils';

/**
 * Truncate hash for display
 * @param hash - The hash to truncate
 * @param startLength - Number of characters to show at start (default: 8)
 * @param endLength - Number of characters to show at end (default: 4)
 * @returns Truncated hash string
 */
export function truncateHash(hash: string | null | undefined, startLength: number = 8, endLength: number = 4): string {
  if (!hash) return 'N/A';
  if (hash.length <= startLength + endLength + 3) return hash;
  return `${hash.slice(0, startLength)}...${hash.slice(-endLength)}`;
}

/**
 * Truncate address for display
 * @param address - The address to truncate
 * @returns Truncated address string
 */
export function truncateAddress(address: string | null | undefined): string {
  return truncateHash(address, 6, 4);
}

/**
 * Format date time to locale string
 * @param dateString - ISO date string
 * @returns Formatted date time string
 */
export function formatDateTime(
  dateInput: DateInput,
  options?: ClientDateTimeOptions,
): string {
  return formatClientDateTime(dateInput, {
    fallback: dateInput == null ? 'N/A' : String(dateInput),
    ...options,
  });
}

/**
 * Format date to locale date string
 * @param dateString - ISO date string
 * @returns Formatted date string
 */
export function formatDate(
  dateInput: DateInput,
  options?: ClientDateTimeOptions,
): string {
  return formatClientDate(dateInput, {
    fallback: dateInput == null ? 'N/A' : String(dateInput),
    ...options,
  });
}

/**
 * Format time to locale time string
 * @param dateString - ISO date string
 * @returns Formatted time string
 */
export function formatTime(
  dateInput: DateInput,
  options?: ClientDateTimeOptions,
): string {
  return formatClientTime(dateInput, {
    fallback: dateInput == null ? 'N/A' : String(dateInput),
    ...options,
  });
}

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 * @param dateString - ISO date string
 * @returns Relative time string
 */
export function formatRelativeTime(
  dateInput: DateInput,
  options?: RelativeTimeOptions,
): string {
  return formatRelativeTimeFromNow(dateInput, {
    fallback: dateInput == null ? 'N/A' : String(dateInput),
    thresholdInMinutes: 1,
    ...options,
  });
}

/**
 * Format number with thousand separators
 * @param value - Number or string value
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted number string
 */
export function formatNumber(value: number | string | null | undefined, decimals: number = 0): string {
  if (value === null || value === undefined) return '0';

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0';

  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format percentage
 * @param value - Number value (0-100)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number | null | undefined, decimals: number = 1): string {
  if (value === null || value === undefined) return '0%';
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format file size
 * @param bytes - Size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Format duration in milliseconds to human-readable string
 * @param ms - Duration in milliseconds
 * @returns Formatted duration string
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

export const sanitizeTokenSymbol = (symbol?: string | null): string => {
  if (typeof symbol !== 'string') {
    return 'UNKNOWN';
  }
  const trimmed = symbol.trim();
  if (!trimmed) {
    return 'UNKNOWN';
  }
  const upper = trimmed.toUpperCase();
  if (upper === 'UNKNOWN' || upper === 'UNKNOW') {
    return 'UNKNOWN';
  }
  return upper;
};

/**
 * Format refund amount with proper token detection
 * @param amount - Amount in smallest unit
 * @param chainId - Chain ID
 * @param tokenAddress - Token contract address
 * @param tokenSymbol - Immutable order token symbol when available
 * @param tokenDecimals - Immutable order token decimals when available
 * @returns Formatted amount string with currency symbol
 */
export function formatRefundAmount(
  amount: string | number,
  _chainId: string | number,
  _tokenAddress?: string,
  tokenSymbol?: string,
  tokenDecimals?: number
): string {
  if (!amount || amount === '0') {
    return '0.00';
  }

  const snapshotSymbol = sanitizeTokenSymbol(tokenSymbol);
  if (
    snapshotSymbol !== 'UNKNOWN'
    && Number.isInteger(tokenDecimals)
    && (tokenDecimals as number) >= 0
    && (tokenDecimals as number) <= 255
  ) {
    const formatted = formatAmountWithDecimals(amount, tokenDecimals as number, 2);
    return `${formatted} ${snapshotSymbol}`;
  }

  const normalizedSymbol = snapshotSymbol;

  try {
    if (typeof amount === 'string' && amount.includes('.')) {
      const numericVal = Number(amount);
      if (!Number.isNaN(numericVal)) {
        return `${numericVal.toFixed(2)} ${normalizedSymbol}`;
      }
    }
  } catch (error) {
    console.error('Failed to format refund amount:', error);
  }

  return normalizedSymbol === 'UNKNOWN' ? 'N/A' : `N/A ${normalizedSymbol}`;
}

/**
 * Get block explorer URL for a transaction
 * @param chainId - Chain ID
 * @param txHash - Transaction hash
 * @returns Explorer URL or undefined
 */
export function getExplorerTxUrl(chainId: string | number | null | undefined, txHash: string): string | undefined {
  if (!chainId || !txHash) return undefined;
  return coreGetExplorerTxUrl(chainId, txHash);
}

/**
 * Get block explorer URL for an address
 * @param chainId - Chain ID
 * @param address - Contract or wallet address
 * @returns Explorer URL or undefined
 */
export function getExplorerAddressUrl(chainId: string | number, address: string): string | undefined {
  if (!chainId || !address) return undefined;
  return coreGetExplorerAddressUrl(chainId, address);
}
